// Responses by time of day: a 7×24 CSS grid (Monday-first rows, hourly
// columns), each cell tinted by intensity — an alpha ramp over the app's own
// accent hue rather than a new sequential ramp, since this is a single-hue
// magnitude encoding and the brand accent already reads as "more of this
// app's own activity" everywhere else. A `title` carries the exact count and
// average score (e.g. "Tue 14:00 — 12 responses, avg 3.4") since color alone
// can't: the whole point of an alpha ramp is that adjacent steps are hard to
// tell apart by eye. The hour axis lives in the same grid as the cells (one
// shared column track, so it can't drift), the caption names the zone — the
// server buckets by UTC, not the operator's clock — and the legend is built
// from the very ramp the cells use. The grid is one `role="img"` whose label
// summarises it (busiest cell), so assistive tech gets the point without
// 168 cells to wade through; the per-cell titles stay as hover detail. It sits
// inside Results' Time of day section card, which carries the frame and title.

import type { SurveyStats } from "@/api/types";
import { heatmapGrid, heatmapMix, heatmapPeak } from "./stats-mapping";

export interface HeatmapCardProps {
    heatmap: SurveyStats["heatmap"];
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Indexed by the wire's `dow` (Sunday=0), for the accessible summary.
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
// One axis label per six columns, each spanning its six hours so it stays
// glued to the column it starts at.
const AXIS_HOURS = [0, 6, 12, 18];
const HOURS_PER_AXIS_LABEL = 6;
// The legend: neutral, then four ramp steps up to the busiest cell.
const LEGEND_STEPS = [0, 0.25, 0.5, 0.75, 1];
const GRID_TEMPLATE_COLUMNS = "2.5rem repeat(24, minmax(16px, 1fr))";

function padHour(hour: number): string {
    return String(hour).padStart(2, "0");
}

function formatHour(hour: number): string {
    return `${padHour(hour)}:00`;
}

/** An empty cell sits on the neutral surface so the grid still reads as a
 *  grid; anything else mixes the accent in along `heatmapMix`'s ramp. */
function cellBackground(intensity: number): string {
    const mix = heatmapMix(intensity);
    return mix === 0 ? "var(--muted)" : `color-mix(in oklch, var(--primary) ${mix}%, transparent)`;
}

function cellTitle(dayLabel: string, hour: number, count: number, averageScore: number | null): string {
    const time = `${dayLabel} ${formatHour(hour)}`;
    if (count === 0) return `${time} — no responses`;
    const avg = averageScore === null ? "—" : averageScore.toFixed(1);
    return `${time} — ${count} response${count === 1 ? "" : "s"}, avg ${avg}`;
}

function gridSummary(heatmap: SurveyStats["heatmap"]): string {
    const lead = "Responses by day of week and hour, UTC";
    const peak = heatmapPeak(heatmap);
    if (peak === null) return `${lead}; no responses in this period`;
    const day = DAY_NAMES[peak.dow] ?? `day ${peak.dow}`;
    return `${lead}; busiest ${day} ${formatHour(peak.hour)} with ${peak.count} response${peak.count === 1 ? "" : "s"}`;
}

export function HeatmapCard({ heatmap }: HeatmapCardProps) {
    const rows = heatmapGrid(heatmap);
    const scoreByKey = new Map(heatmap.map((cell) => [`${cell.dow}:${cell.hour}`, cell.averageScore]));

    return (
        <div>
            <div className="overflow-x-auto">
                <div
                    role="img"
                    aria-label={gridSummary(heatmap)}
                    className="grid w-full min-w-max gap-[2px]"
                    style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                >
                    {rows.map((row, rowIndex) => {
                        const dayLabel = DAY_LABELS[rowIndex] ?? "";
                        return (
                            // display:contents so each cell below is a direct
                            // grid item of the outer grid (one shared 24-column
                            // track) instead of nesting a grid-per-row.
                            <div key={row[0]?.dow ?? rowIndex} className="contents">
                                <span className="flex items-center pr-2 text-xs text-muted-foreground">{dayLabel}</span>
                                {row.map((cell) => {
                                    const averageScore = scoreByKey.get(`${cell.dow}:${cell.hour}`) ?? null;
                                    return (
                                        <div
                                            key={`${cell.dow}-${cell.hour}`}
                                            data-testid="heatmap-cell"
                                            className="aspect-square rounded-[2px]"
                                            style={{ backgroundColor: cellBackground(cell.intensity) }}
                                            title={cellTitle(dayLabel, cell.hour, cell.count, averageScore)}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                    {/* The hour axis, in the same grid as the cells: the first
                        span sits under the day-label column, the rest each
                        cover six hourly columns. */}
                    <div className="contents">
                        <span />
                        {AXIS_HOURS.map((hour) => (
                            <span
                                key={hour}
                                className="pt-1 text-xs text-muted-foreground"
                                style={{ gridColumn: `span ${HOURS_PER_AXIS_LABEL}` }}
                            >
                                {padHour(hour)}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <p>Hour of day (UTC)</p>
                <p className="flex items-center gap-1">
                    <span>Fewer</span>
                    {LEGEND_STEPS.map((step) => (
                        <span
                            key={step}
                            data-testid="heatmap-legend-swatch"
                            className="inline-block size-3 rounded-[2px]"
                            style={{ backgroundColor: cellBackground(step) }}
                        />
                    ))}
                    <span>More</span>
                </p>
            </div>
        </div>
    );
}
